import React from 'react';
import { useUserData, ExerciseType } from '@/context/UserDataContext';
import { cn } from '@/lib/utils';
import { EXERCISE_TYPES } from '../../shared/constants';
import { FormSection } from '../../shared/FormSection';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ExerciseTypeToggleProps {
  error?: string;
}

const ExerciseTypeToggle: React.FC<ExerciseTypeToggleProps> = ({ error }) => {
  const { userData, setUserData } = useUserData();

  const handleExerciseTypeToggle = (type: ExerciseType) => {
    setUserData(prev => {
      const currentTypes = prev.exerciseType as ExerciseType[];
      const isSelected = currentTypes.includes(type);
      
      return {
        ...prev,
        exerciseType: isSelected
          ? currentTypes.filter(t => t !== type)
          : [...currentTypes, type]
      };
    });
  };

  return (
    <FormSection
      question="What types of exercise do you typically do?"
      helperText="Select all that apply."
      error={error}
    >
      <div className="grid grid-cols-2 gap-2 py-2">
        {EXERCISE_TYPES.slice(0, 4).map((type) => {
          const isSelected = (userData.exerciseType as ExerciseType[]).includes(type.value);
          return (
            <TooltipProvider key={type.value} delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <div
                      onClick={() => handleExerciseTypeToggle(type.value)}
                      className={cn(
                        "w-full flex flex-col items-center justify-center h-24 p-2 rounded-2xl cursor-pointer border-2 transition-all border",
                        "hover:border-primary/30 hover:bg-primary/5",
                        isSelected
                          ? "border-primary bg-primary/5 shadow-sm text-primary"
                          : "border-border bg-background text-muted-foreground/90"
                      )}
                    >
                      <div className="flex flex-col items-center mb-2">
                        {type.icon}
                      </div>
                      <span className={cn(
                        "text-sm font-medium transition-colors duration-300 text-center",
                        isSelected ? "text-primary" : "text-muted-foreground/80"
                      )}>
                        {type.label}
                      </span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className='mb-4 max-w-[300px] p-4'>
                  <p className="text-sm">
                    {type.description}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </FormSection>
  );
};

export default ExerciseTypeToggle; 